import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';

function ExerciseLibrary() {
  const [user, setUser] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        fetchExercises();
      } else {
        navigate('/');
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user);
        fetchExercises();
      } else {
        navigate('/');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    filterExercises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises, searchTerm, selectedMuscle, selectedEquipment]);

  const fetchExercises = async () => {
    try {
      const { data, error } = await supabase
        .from('exercise_library')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching exercises:', error);
      } else {
        setExercises(data || []);
        
        // Extract unique muscle groups and equipment types
        const muscles = [...new Set(data?.map(ex => ex.primary_muscle).filter(Boolean) || [])].sort();
        const equipment = [...new Set(data?.map(ex => ex.equipment).filter(Boolean) || [])].sort();
        setMuscleGroups(muscles);
        setEquipmentTypes(equipment);
      }
    } catch (err) {
      console.error('Error fetching exercises:', err);
    }
  };

  const filterExercises = () => {
    let filtered = exercises;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(exercise =>
        exercise.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exercise.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by muscle group
    if (selectedMuscle) {
      filtered = filtered.filter(exercise => exercise.primary_muscle === selectedMuscle);
    }

    // Filter by equipment
    if (selectedEquipment) {
      filtered = filtered.filter(exercise => exercise.equipment === selectedEquipment);
    }

    setFilteredExercises(filtered);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const renderMedia = (media) => {
    if (!media) return null;

    if (Array.isArray(media)) {
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {media.map((item, index) => (
            <div key={index}>
              {item.type === 'image' && item.url && (
                <img
                  src={item.url}
                  alt={item.alt || 'Exercise media'}
                  className="w-32 h-32 object-cover rounded"
                />
              )}
              {item.type === 'video' && item.url && (
                <video
                  src={item.url}
                  className="w-32 h-32 object-cover rounded"
                  controls
                />
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof media === 'object') {
      if (media.url) {
        return (
          <div className="mt-2">
            {media.type === 'video' ? (
              <video src={media.url} className="w-full max-w-md rounded" controls />
            ) : (
              <img
                src={media.url}
                alt={media.alt || 'Exercise media'}
                className="w-full max-w-md rounded"
              />
            )}
          </div>
        );
      }
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Exercise Library</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-600">{user?.email}</span>
              <button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search exercises..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Muscle Group Filter */}
            <div>
              <label htmlFor="muscle" className="block text-sm font-medium text-gray-700 mb-2">
                Primary Muscle
              </label>
              <select
                id="muscle"
                value={selectedMuscle}
                onChange={(e) => setSelectedMuscle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Muscles</option>
                {muscleGroups.map((muscle) => (
                  <option key={muscle} value={muscle}>
                    {muscle}
                  </option>
                ))}
              </select>
            </div>

            {/* Equipment Filter */}
            <div>
              <label htmlFor="equipment" className="block text-sm font-medium text-gray-700 mb-2">
                Equipment
              </label>
              <select
                id="equipment"
                value={selectedEquipment}
                onChange={(e) => setSelectedEquipment(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Equipment</option>
                {equipmentTypes.map((equipment) => (
                  <option key={equipment} value={equipment}>
                    {equipment}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredExercises.length} of {exercises.length} exercises
          </div>
        </div>

        {/* Exercise Grid */}
        {filteredExercises.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500">No exercises found matching your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExercises.map((exercise) => (
              <div
                key={exercise.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {exercise.name}
                </h3>

                {exercise.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {exercise.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  {exercise.primary_muscle && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">Muscle:</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {exercise.primary_muscle}
                      </span>
                    </div>
                  )}

                  {exercise.equipment && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">Equipment:</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        {exercise.equipment}
                      </span>
                    </div>
                  )}
                </div>

                {renderMedia(exercise.media)}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default ExerciseLibrary;

